'use client';

import React from 'react';

import { Heading } from '@depop/web-thrift-ui/Typography';
import { useTranslations } from 'next-intl';

import { PlaceholderPills } from '../PlaceholderPills';
import styles from '../styles.module.css';

import { Pill } from '@/components/Pill';
import { PlaceHolderShape } from '@/components/Placeholder';
import {
  ActivityTrackerEventType,
  ModuleNames,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { type Brand } from '@/modules/brands/aliases';
import { MAX_POPULAR_BRANDS } from '@/modules/brands/constants.ts';
import { useBrands } from '@/modules/brands/hooks/useBrands';
import { buildPopularBrands } from '@/modules/brands/utils';
import { useSearchFilterAggregates } from '@/modules/search/useSearchFilterAggregates';
import { useSearchFilters } from '@/modules/search/useSearchFilters';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  allowedBrandSlugs?: string[];
};

export function PopularBrands({ allowedBrandSlugs }: Props) {
  const {
    searchFilters: { brands },
    setSearchFilters: { setBrandFilter },
  } = useSearchFilters();
  const t = useTranslations('common');
  const { data: aggregates, isLoading: isFilterAggregateDataLoading } =
    useSearchFilterAggregates({});
  const { data: brandsData, isLoading: isBrandsDataLoading } = useBrands();
  const [, user] = useCurrentUser();
  const isLoading = isFilterAggregateDataLoading || isBrandsDataLoading;

  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: user?.id,
  });

  if (isLoading) {
    return (
      <PlaceholderPills
        shape={PlaceHolderShape.PILL}
        title={t('Nav.PopularBrands')}
      />
    );
  }

  if (!brandsData || !aggregates?.data.brands) {
    return null;
  }

  function handleClick(brand: Brand, index: number) {
    sendActivityTrackerEvent(ActivityTrackerEventType.MODULE_CLICK_ACTION, {
      moduleName: ModuleNames.BrandSuggestions,
      schemaVersion: '3.0',
      itemClicked: brand.name,
      itemId: String(brand.id),
      itemPosition: index,
    });
    setBrandFilter(String(brand.id));
  }

  const popularBrands = buildPopularBrands({
    allowedBrandSlugs,
    brands: brandsData.data,
    brandAggregates: aggregates?.data.brands,
    maxCount: MAX_POPULAR_BRANDS,
  });

  if (!popularBrands.length) {
    return null;
  }

  return (
    <div className={styles.popularContainer}>
      <Heading variant="sm" className={styles.popularTitle}>
        {t('Nav.PopularBrands')}
      </Heading>
      <div className={styles.popularPillsContainer}>
        {popularBrands.map((brand, index) => (
          <Pill
            key={brand.id}
            isActive={Boolean(brands?.includes(String(brand.id)))}
            onClick={() => handleClick(brand, index)}
          >
            {brand.name}
          </Pill>
        ))}
      </div>
    </div>
  );
}
